import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import "flatpickr/dist/themes/material_blue.css";
import { toast, ToastContainer } from "react-toastify";

import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollToTop from "../components/ScrollTop";
import PhoneLoader from "../components/loader/PhoneLoader";
import { useAuth } from "../provider/authProvider";

import "../SmsCampaigns.css";

const API = process.env.REACT_APP_API || "";

const SEGMENT_OPTIONS = [
    { value: "payjoy", label: "PayJoy" },
    { value: "movilespay", label: "MovilesPay" },
    { value: "credismart", label: "CrediSmart" },
    { value: "efectiva", label: "Efectiva" },
];

const MANUAL_SEGMENT_OPTIONS = [
    { value: "Manual", label: "Manual" },
    { value: "PayJoy", label: "PayJoy" },
    { value: "MovilesPay", label: "MovilesPay" },
    { value: "CrediSmart", label: "CrediSmart" },
    { value: "Efectiva", label: "Efectiva" },
];

const RANKING_OPTIONS = [
    { value: "MB", label: "Muy Bueno - MB" },
    { value: "B", label: "Bueno - B" },
    { value: "R", label: "Regular - R" },
    { value: "M", label: "Malo - M" },
    { value: "MM", label: "Muy Malo - MM" },
    { value: "P", label: "Pérdida - P" },
];

const STATUS_OPTIONS = [
    { value: "ALL", label: "Todas" },
    { value: "sent", label: "Enviadas" },
    { value: "scheduled", label: "Programadas" },
    { value: "failed", label: "Fallidas" },
    { value: "sending", label: "Enviando" },
];

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 44,
        borderRadius: 14,
        borderColor: state.isFocused ? "#6d28d9" : "#dbe3ef",
        boxShadow: state.isFocused ? "0 0 0 4px rgba(109,40,217,.12)" : "none",
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function formatDate(date) {
    if (!date) return "";
    const d = new Date(date);

    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
    )}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateTime(value) {
    if (!value) return "-";

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;

    return d.toLocaleString("es-PE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
}

function onlyDigits(value = "") {
    return String(value).replace(/\D/g, "");
}

function normalizePhone(value = "") {
    const phone = onlyDigits(value);

    if (phone.length === 9 && phone.startsWith("9")) return `51${phone}`;
    if (phone.length === 11 && phone.startsWith("51")) return phone;

    return null;
}

function mergeUniquePhones(currentPhones = [], newPhones = []) {
    const map = new Map();

    [...currentPhones, ...newPhones].forEach((item) => {
        const normalized = normalizePhone(item.phoneNumber || item.rawPhone);
        if (!normalized) return;

        if (!map.has(normalized)) {
            map.set(normalized, {
                ...item,
                phoneNumber: normalized,
                rawPhone: item.rawPhone || item.phoneNumber || "",
            });
        }
    });

    return Array.from(map.values());
}

const initialManualForm = {
    clientName: "",
    phoneNumber: "",
    dni: "",
    segment: MANUAL_SEGMENT_OPTIONS[0],
};

export default function SmsCampaigns(props) {
    const { user } = useAuth();

    const rangeRef = useRef(null);
    const sendAtRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([]);
    const [sendAt, setSendAt] = useState("");
    const [segments, setSegments] = useState([]);
    const [rankings, setRankings] = useState([]);
    const [message, setMessage] = useState("");
    const [campaignName, setCampaignName] = useState("");
    const [phones, setPhones] = useState([]);
    const [manualForm, setManualForm] = useState(initialManualForm);

    const [history, setHistory] = useState([]);
    const [historyKpis, setHistoryKpis] = useState({});
    const [historyPagination, setHistoryPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    });
    const [historyStatus, setHistoryStatus] = useState(STATUS_OPTIONS[0]);
    const [historySearch, setHistorySearch] = useState("");
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [showDetail, setShowDetail] = useState(false);

    useEffect(() => {
        const fp = flatpickr(rangeRef.current, {
            locale: Spanish,
            mode: "range",
            dateFormat: "Y-m-d",
            closeOnSelect: false,
            onChange: (dates) => {
                setDateRange(dates || []);
            },
        });

        const fpSend = flatpickr(sendAtRef.current, {
            locale: Spanish,
            enableTime: true,
            enableSeconds: true,
            time_24hr: true,
            dateFormat: "Y-m-d H:i:S",
            minDate: "today",
            onChange: (_dates, dateStr) => setSendAt(dateStr),
        });

        return () => {
            fp.destroy();
            fpSend.destroy();
        };
    }, []);

    useEffect(() => {
        setPhones([]);
    }, [dateRange, segments, rankings]);

    const buildPreviewPayload = () => {
        const startDate = dateRange[0] ? formatDate(dateRange[0]) : "";
        const endDate = dateRange[1] ? formatDate(dateRange[1]) : startDate;

        return {
            startDate,
            endDate,
            segments: Array.isArray(segments) ? segments.map((s) => s.value) : [],
            rankings: Array.isArray(rankings) ? rankings.map((r) => r.value) : [],
        };
    };

    const loadHistory = async (page = 1) => {
        try {
            const res = await axios.get(`${API}/sms-campaigns/history`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                    user,
                },
                params: {
                    page,
                    limit: 20,
                    status: historyStatus?.value || "ALL",
                    search: historySearch,
                },
            });

            setHistory(res.data.data || []);
            setHistoryKpis(res.data.kpis || {});
            setHistoryPagination(res.data.pagination || {});
        } catch (error) {
            toast.error(
                error?.response?.data?.error || "No se pudo cargar el historial.",
            );
        }
    };

    useEffect(() => {
        loadHistory(1);
    }, [historyStatus]);

    const preview = async () => {
        const currentPayload = buildPreviewPayload();

        if (!currentPayload.startDate || !currentPayload.endDate) {
            setPhones([]);
            toast.warning("Selecciona un rango de fechas.");
            return;
        }

        if (!currentPayload.segments.length) {
            setPhones([]);
            toast.warning("Selecciona al menos una financiera.");
            return;
        }

        try {
            setLoading(true);
            setPhones([]);

            const res = await axios.post(
                `${API}/sms-campaigns/preview`,
                currentPayload,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                        user,
                    },
                },
            );

            const filteredPhones = res.data.data || [];
            setPhones(filteredPhones);

            toast.success(`Se encontraron ${res.data.total || 0} números únicos.`);
        } catch (error) {
            setPhones([]);
            toast.error(
                error?.response?.data?.error || "No se pudo filtrar la campaña.",
            );
        } finally {
            setLoading(false);
        }
    };

    const addManualPhone = () => {
        const cleanPhone = normalizePhone(manualForm.phoneNumber);

        if (!manualForm.clientName.trim()) {
            toast.warning("Ingresa el nombre del cliente.");
            return;
        }

        if (!cleanPhone) {
            toast.warning(
                "Ingresa un número válido. Ejemplo: 9XXXXXXXX o 519XXXXXXXX.",
            );
            return;
        }

        if (manualForm.dni && !/^\d{8}$/.test(onlyDigits(manualForm.dni))) {
            toast.warning("El DNI debe tener 8 dígitos.");
            return;
        }

        const newItem = {
            phoneNumber: cleanPhone,
            rawPhone: manualForm.phoneNumber,
            clientName: manualForm.clientName.trim(),
            dni: onlyDigits(manualForm.dni),
            segment: manualForm.segment?.value || "Manual",
            ranking: null,
            source: "manual",
            isManual: true,
        };

        const before = phones.length;
        const merged = mergeUniquePhones(phones, [newItem]);

        setPhones(merged);

        if (merged.length === before) {
            toast.info("Este número ya existe en la lista.");
            return;
        }

        setManualForm(initialManualForm);
        toast.success("Número manual agregado correctamente.");
    };

    const removePhone = (phoneNumber) => {
        setPhones((prev) =>
            prev.filter((item) => item.phoneNumber !== phoneNumber),
        );
        toast.info("Número retirado de la lista.");
    };

    const clearManualFields = () => {
        setManualForm(initialManualForm);
    };

    const sendNow = async () => {
        if (!message.trim()) {
            toast.warning("Escribe el mensaje SMS.");
            return;
        }

        if (!phones.length) {
            toast.warning("Primero filtra o agrega números manuales.");
            return;
        }

        try {
            setLoading(true);

            await axios.post(
                `${API}/sms-campaigns/send-now`,
                { phones, message, campaignName },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                        user,
                    },
                },
            );

            toast.success("Campaña enviada correctamente.");
            setCampaignName("");
            setMessage("");
            setPhones([]);
            loadHistory(1);
        } catch (error) {
            toast.error(
                error?.response?.data?.error || "No se pudo enviar la campaña.",
            );
        } finally {
            setLoading(false);
        }
    };

    const scheduleCampaign = async () => {
        if (!sendAt) {
            toast.warning("Selecciona fecha y hora de envío.");
            return;
        }

        if (!message.trim()) {
            toast.warning("Escribe el mensaje SMS.");
            return;
        }

        if (!phones.length) {
            toast.warning("Primero filtra o agrega números manuales.");
            return;
        }

        try {
            setLoading(true);

            await axios.post(
                `${API}/sms-campaigns/schedule`,
                {
                    phones,
                    message,
                    sendAt,
                    campaignName,
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                        user,
                    },
                },
            );

            toast.success("Campaña programada correctamente.");
            setCampaignName("");
            setMessage("");
            setPhones([]);
            loadHistory(1);
        } catch (error) {
            toast.error(
                error?.response?.data?.error || "No se pudo programar la campaña.",
            );
        } finally {
            setLoading(false);
        }
    };

    const openCampaignDetail = async (id) => {
        try {
            setLoading(true);

            const res = await axios.get(`${API}/sms-campaigns/history/${id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                    user,
                },
            });

            setSelectedCampaign(res.data.data);
            setShowDetail(true);
        } catch (error) {
            toast.error(
                error?.response?.data?.error || "No se pudo abrir el detalle.",
            );
        } finally {
            setLoading(false);
        }
    };

    const getStatusClass = (status) => {
        return `smsStatusBadge smsStatus-${status || "default"}`;
    };

    return (
        <div>
            <Header user={user} namePage={props.namePage || "SMS Campaigns"} />

            <section className="sectionBody sectionBodySmsCampaigns">
                <div className="containerCustom containerBody">
                    <div className="smsCampaignBlock">
                        <div className="smsCampaignHero">
                            <div>
                                <span>Marketing SMS</span>
                                <h1>SMS Campaigns</h1>
                                <p>
                                    Filtra clientes, agrega números manuales, elimina duplicados y
                                    envía campañas por Labsmobile.
                                </p>
                            </div>

                            <div className="smsCampaignCounter">
                                <strong>{phones.length}</strong>
                                <small>números únicos</small>
                            </div>
                        </div>

                        <div className="smsField smsCampaignNameField">
                            <label>Nombre de campaña</label>
                            <input
                                value={campaignName}
                                onChange={(e) => setCampaignName(e.target.value)}
                                placeholder="Ejemplo: Día de la Madre - Renovación"
                                maxLength={160}
                            />
                        </div>

                        <div className="smsFiltersGrid">
                            <div className="smsField">
                                <label>Rango de fechas</label>
                                <input
                                    ref={rangeRef}
                                    placeholder="Selecciona fecha inicio y fin"
                                />
                            </div>

                            <div className="smsField">
                                <label>Financieras</label>
                                <Select
                                    isMulti
                                    options={SEGMENT_OPTIONS}
                                    value={segments}
                                    onChange={(v) => setSegments(v || [])}
                                    styles={selectStyles}
                                    placeholder="Seleccionar financieras"
                                    noOptionsMessage={() => "No hay financieras"}
                                    menuPortalTarget={document.body}
                                />
                            </div>

                            <div className="smsField">
                                <label>Ranking de pagos MovilesPay</label>
                                <Select
                                    isMulti
                                    options={RANKING_OPTIONS}
                                    value={rankings}
                                    onChange={(v) => setRankings(v || [])}
                                    styles={selectStyles}
                                    placeholder="Opcional"
                                    noOptionsMessage={() => "No hay rankings"}
                                    menuPortalTarget={document.body}
                                />
                                <small>Este filtro solo aplica para clientes MovilesPay.</small>
                            </div>

                            <div className="smsField">
                                <label>Fecha programada</label>
                                <input ref={sendAtRef} placeholder="YYYY-MM-DD HH:mm:ss" />
                            </div>
                        </div>

                        <div className="smsActions">
                            <button
                                type="button"
                                onClick={preview}
                                className="smsBtn smsBtnGhost"
                                disabled={loading}
                            >
                                Filtrar registros
                            </button>

                            <button
                                type="button"
                                onClick={scheduleCampaign}
                                className="smsBtn smsBtnPrimary"
                                disabled={loading}
                            >
                                Programar envío
                            </button>

                            <button
                                type="button"
                                onClick={sendNow}
                                className="smsBtn smsBtnNow"
                                disabled={loading}
                            >
                                Enviar ahora
                            </button>
                        </div>

                        <div className="smsManualBox">
                            <div className="smsManualHeader">
                                <div>
                                    <strong>Agregar número manual</strong>
                                    <p>
                                        Adjunta clientes externos a la misma lista de envío. El
                                        sistema evita números duplicados.
                                    </p>
                                </div>
                            </div>

                            <div className="smsManualGrid">
                                <div className="smsField">
                                    <label>Cliente</label>
                                    <input
                                        value={manualForm.clientName}
                                        onChange={(e) =>
                                            setManualForm((prev) => ({
                                                ...prev,
                                                clientName: e.target.value,
                                            }))
                                        }
                                        placeholder="Nombre del cliente"
                                    />
                                </div>

                                <div className="smsField">
                                    <label>Número</label>
                                    <input
                                        value={manualForm.phoneNumber}
                                        onChange={(e) =>
                                            setManualForm((prev) => ({
                                                ...prev,
                                                phoneNumber: e.target.value,
                                            }))
                                        }
                                        placeholder="9XXXXXXXX"
                                        maxLength={11}
                                    />
                                </div>

                                <div className="smsField">
                                    <label>DNI</label>
                                    <input
                                        value={manualForm.dni}
                                        onChange={(e) =>
                                            setManualForm((prev) => ({
                                                ...prev,
                                                dni: onlyDigits(e.target.value),
                                            }))
                                        }
                                        placeholder="Opcional"
                                        maxLength={8}
                                    />
                                </div>

                                <div className="smsField">
                                    <label>Financiera</label>
                                    <Select
                                        options={MANUAL_SEGMENT_OPTIONS}
                                        value={manualForm.segment}
                                        onChange={(v) =>
                                            setManualForm((prev) => ({
                                                ...prev,
                                                segment: v || MANUAL_SEGMENT_OPTIONS[0],
                                            }))
                                        }
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                    />
                                </div>
                            </div>

                            <div className="smsManualActions">
                                <button
                                    type="button"
                                    className="smsBtn smsBtnPrimary"
                                    onClick={addManualPhone}
                                    disabled={loading}
                                >
                                    Agregar a la lista
                                </button>

                                <button
                                    type="button"
                                    className="smsBtn smsBtnGhost"
                                    onClick={clearManualFields}
                                    disabled={loading}
                                >
                                    Limpiar campos
                                </button>
                            </div>
                        </div>

                        <div className="smsMessageBox">
                            <label>Mensaje SMS</label>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                maxLength={480}
                                placeholder="Escribe el mensaje que se enviará a los clientes..."
                            />
                            <div className="smsMessageMeta">
                                <span>{message.length}/480 caracteres</span>
                                <span>{phones.length} destinatarios</span>
                            </div>
                        </div>

                        <div className="smsPreviewTableWrap">
                            <table className="smsPreviewTable">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Número</th>
                                        <th>Cliente</th>
                                        <th>DNI</th>
                                        <th>Financiera</th>
                                        <th>Ranking</th>
                                        <th>Fuente</th>
                                        <th>Acción</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {phones.length ? (
                                        phones.map((item, index) => (
                                            <tr
                                                key={`${item.phoneNumber}-${index}`}
                                                className={item.isManual ? "smsManualRow" : ""}
                                            >
                                                <td>{index + 1}</td>
                                                <td>{item.phoneNumber}</td>
                                                <td>{item.clientName}</td>
                                                <td>{item.dni || "-"}</td>
                                                <td>{item.segment || "-"}</td>
                                                <td>{item.ranking || "-"}</td>
                                                <td>{item.source}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="smsRemoveBtn"
                                                        onClick={() => removePhone(item.phoneNumber)}
                                                        disabled={loading}
                                                    >
                                                        Quitar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="smsEmpty">
                                                No hay números filtrados ni agregados manualmente.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="smsHistorySection">
                            <div className="smsHistoryHeader">
                                <div>
                                    <span>Historial</span>
                                    <h2>Campañas realizadas</h2>
                                    <p>
                                        Consulta envíos inmediatos, programados, fallidos y
                                        respuesta de Labsmobile.
                                    </p>
                                </div>

                                <div className="smsHistoryFilters">
                                    <input
                                        value={historySearch}
                                        onChange={(e) => setHistorySearch(e.target.value)}
                                        placeholder="Buscar campaña o mensaje"
                                    />

                                    <Select
                                        options={STATUS_OPTIONS}
                                        value={historyStatus}
                                        onChange={(v) => setHistoryStatus(v || STATUS_OPTIONS[0])}
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                    />

                                    <button
                                        type="button"
                                        className="smsBtn smsBtnGhost"
                                        onClick={() => loadHistory(1)}
                                        disabled={loading}
                                    >
                                        Buscar
                                    </button>
                                </div>
                            </div>

                            <div className="smsHistoryKpis">
                                <div className="smsHistoryKpi">
                                    <span>Total campañas</span>
                                    <strong>{historyKpis.totalCampaigns || 0}</strong>
                                </div>
                                <div className="smsHistoryKpi">
                                    <span>Total destinatarios</span>
                                    <strong>{historyKpis.totalRecipients || 0}</strong>
                                </div>
                                <div className="smsHistoryKpi smsHistoryKpiSent">
                                    <span>Enviadas</span>
                                    <strong>{historyKpis.sentCampaigns || 0}</strong>
                                </div>
                                <div className="smsHistoryKpi smsHistoryKpiScheduled">
                                    <span>Programadas</span>
                                    <strong>{historyKpis.scheduledCampaigns || 0}</strong>
                                </div>
                                <div className="smsHistoryKpi smsHistoryKpiFailed">
                                    <span>Fallidas</span>
                                    <strong>{historyKpis.failedCampaigns || 0}</strong>
                                </div>
                            </div>

                            <div className="smsHistoryTableWrap">
                                <table className="smsHistoryTable">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Campaña</th>
                                            <th>Estado</th>
                                            <th>Tipo</th>
                                            <th>Clientes</th>
                                            <th>Fecha programada</th>
                                            <th>Fecha enviada</th>
                                            <th>Labsmobile</th>
                                            <th>Creado por</th>
                                            <th>Acción</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {history.length ? (
                                            history.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        <strong>
                                                            {item.campaignName || `Campaña #${item.id}`}
                                                        </strong>
                                                        <small>
                                                            {String(item.message || "").slice(0, 70)}...
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <span className={getStatusClass(item.status)}>
                                                            {item.statusLabel}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {item.sendType === "now"
                                                            ? "Inmediato"
                                                            : "Programado"}
                                                    </td>
                                                    <td>{item.totalPhones}</td>
                                                    <td>{formatDateTime(item.sendAt)}</td>
                                                    <td>{formatDateTime(item.sentAt)}</td>
                                                    <td>
                                                        {item.labsmobileCode || "-"}
                                                        {item.labsmobileMessage ? (
                                                            <small>{item.labsmobileMessage}</small>
                                                        ) : null}
                                                    </td>
                                                    <td>{item.creatorFullName || "-"}</td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="smsDetailBtn"
                                                            onClick={() => openCampaignDetail(item.id)}
                                                            disabled={loading}
                                                        >
                                                            Ver detalle
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="10" className="smsEmpty">
                                                    No hay campañas registradas.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="smsHistoryPagination">
                                <button
                                    type="button"
                                    className="smsBtn smsBtnGhost"
                                    disabled={Number(historyPagination.page) <= 1 || loading}
                                    onClick={() =>
                                        loadHistory(Number(historyPagination.page) - 1)
                                    }
                                >
                                    Anterior
                                </button>

                                <span>
                                    Página {historyPagination.page || 1} de{" "}
                                    {historyPagination.totalPages || 1}
                                </span>

                                <button
                                    type="button"
                                    className="smsBtn smsBtnGhost"
                                    disabled={
                                        Number(historyPagination.page) >=
                                        Number(historyPagination.totalPages || 1) || loading
                                    }
                                    onClick={() =>
                                        loadHistory(Number(historyPagination.page) + 1)
                                    }
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {showDetail && selectedCampaign ? (
                <div className="smsModalOverlay" onClick={() => setShowDetail(false)}>
                    <div className="smsModal" onClick={(e) => e.stopPropagation()}>
                        <div className="smsModalHeader">
                            <div>
                                <span>Detalle de campaña</span>
                                <h3>
                                    {selectedCampaign.campaignName ||
                                        `Campaña #${selectedCampaign.id}`}
                                </h3>
                            </div>
                            <button type="button" onClick={() => setShowDetail(false)}>
                                ×
                            </button>
                        </div>

                        <div className="smsDetailGrid">
                            <div>
                                <span>Estado</span>
                                <strong>{selectedCampaign.statusLabel}</strong>
                            </div>
                            <div>
                                <span>Total clientes</span>
                                <strong>{selectedCampaign.totalPhones}</strong>
                            </div>
                            <div>
                                <span>Fecha programada</span>
                                <strong>{formatDateTime(selectedCampaign.sendAt)}</strong>
                            </div>
                            <div>
                                <span>Fecha enviada</span>
                                <strong>{formatDateTime(selectedCampaign.sentAt)}</strong>
                            </div>
                            <div>
                                <span>Tipo</span>
                                <strong>
                                    {selectedCampaign.sendType === "now"
                                        ? "Inmediato"
                                        : "Programado"}
                                </strong>
                            </div>
                            <div>
                                <span>Labsmobile</span>
                                <strong>{selectedCampaign.labsmobileCode || "-"}</strong>
                            </div>
                        </div>

                        <div className="smsDetailMessage">
                            <label>Mensaje enviado</label>
                            <p>{selectedCampaign.message}</p>
                        </div>

                        <div className="smsPreviewTableWrap smsDetailPhonesWrap">
                            <table className="smsPreviewTable">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Número</th>
                                        <th>Cliente</th>
                                        <th>DNI</th>
                                        <th>Financiera</th>
                                        <th>Ranking</th>
                                        <th>Fuente</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {selectedCampaign.phones?.map((item, index) => (
                                        <tr key={`${item.phoneNumber}-${index}`}>
                                            <td>{index + 1}</td>
                                            <td>{item.phoneNumber}</td>
                                            <td>{item.clientName}</td>
                                            <td>{item.dni || "-"}</td>
                                            <td>{item.segment || "-"}</td>
                                            <td>{item.ranking || "-"}</td>
                                            <td>{item.source || "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : null}

            {loading && <PhoneLoader />}
            <ScrollToTop />
            <Footer />
        </div>
    );
}
